import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Avatar, Badge, Btn, Card, Overline, Select } from "../../ForBrands/ui/BrandUI";
import { ListRule, MetaCols, MobileCard, MobileEmpty, MobilePageHead } from "../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import { CHART } from "../../ForBrands/ui/charts/chartTokens";
import { OUT_OF_10, fmtIndex10 } from "../../../utils/indexScore";
import AthleteHoverCard from "./AthleteHoverCard";
import {
  ARCHETYPE_META,
  EMPTY_VALUE,
  PLATFORM_LABELS,
  STATUS_COLORS,
  fitTone,
  fmtMoney,
  fmtMoneyCompact,
  initialsOf,
  niceTicks,
  playerSubtitle,
  plural,
  rosterClassOf,
  swingArchetypes,
  swingPoints,
  teamKey,
  teamOptionLabel,
  teamsForView,
  trendLabel,
} from "./coachUtils";

// Coach Mode V2 building blocks shared by Home, Team and Rep Mode (Figma
// COACH MODE V2). Pieces with a phone look (Figma COACH MODE V2 mobile)
// switch on usePhoneTheme(), so they only change inside the Coach Mode shell
// below 640px; Rep Mode has no phone theme and keeps today's markup.

// The phone card chrome (Figma 2406:932, 2406:962): 16px radius and the
// subtle border, where the desktop Card has 12px and border-bm-border.
// Callers add the vertical padding and the inner gap.
export const PHONE_CARD = "rounded-[16px] border border-bm-mutedbg bg-white px-[18px]";

// The placeholder for a figure a row has no data for.
const Muted = () => <span className="bm-body-m text-bm-tertiary">{EMPTY_VALUE}</span>;

// Player cell (Figma 02 · Team Roster): 32px avatar, name, "PG · #23".
// `onClick` makes it a button.
export const PlayerCell = ({ p, onClick }) => {
  const Tag = onClick ? "button" : "span";
  return (
    <Tag {...(onClick ? { type: "button", onClick } : {})} className="flex items-center gap-[10px] text-left">
      <Avatar src={p.avatar} initials={initialsOf(p)} size={32} tone="outline" />
      <span className="flex min-w-0 flex-col">
        <span className="bm-h-xs text-bm-ink">
          {p.name} {p.surname}
        </span>
        <span className="bm-body-s text-bm-muted">{playerSubtitle(p) || EMPTY_VALUE}</span>
      </span>
    </Tag>
  );
};

// "↗ +4" in green, "↘ −2" in red; `suffix` follows the number ("pp").
// `type` is the text style (the phone roster card uses bm-body-xs).
export const TrendCell = ({ trend, suffix = "", type = "bm-label-l" }) => {
  const t = trendLabel(trend);
  const color = t.tone === "up" ? "text-bm-positive" : t.tone === "down" ? "text-bm-critical" : "text-bm-tertiary";
  return (
    <span className={`${type} ${color}`}>
      {t.arrow ? `${t.arrow} ` : ""}
      {t.text}
      {t.arrow ? suffix : ""}
    </span>
  );
};

export const StatusDot = ({ status, title }) => (
  <span
    title={title || (status === "active" ? "Active" : "Pending activation")}
    className="inline-block h-[18px] w-[18px] rounded-full"
    style={{ background: status === "active" ? STATUS_COLORS.active : STATUS_COLORS.pending }}
  />
);

// Phones space the two badges 8px apart (2406:844, 2407:956).
export const StatusLegend = () => {
  const phone = usePhoneTheme();
  return (
    <div className={`flex items-center justify-end ${phone ? "gap-2" : "gap-5"}`}>
      <Badge tone="outline" dot={STATUS_COLORS.active}>
        Active
      </Badge>
      <Badge tone="outline" dot={STATUS_COLORS.pending}>
        Pending
      </Badge>
    </div>
  );
};

export const ArchetypeBadge = ({ archetype }) => {
  const meta = ARCHETYPE_META[archetype];
  if (!meta) return <Muted />;
  return (
    <Badge tone="outline" dot={meta.color}>
      {meta.label}
    </Badge>
  );
};

// A number as the row has it (the CacheScore, out of 100).
const Pillar = ({ value }) => (value == null ? <Muted /> : <span className="bm-num-m text-bm-ink">{value}</span>);

// Performance, Visibility or Off-court out of 10 ("6.1", utils/indexScore;
// the column head says "(out of 10)"); the en dash for one never computed.
const IndexPillar = ({ value }) => (value == null ? <Muted /> : <span className="bm-num-m text-bm-ink">{fmtIndex10(value)}</span>);

// A pillar figure on a phone card, where no column head says the scale:
// "6.1/10", or nothing (the card's en dash) for one never computed.
const indexFigure = (value) => (value == null ? null : fmtIndex10(value, { suffix: true }));

// A pillar column's head: its name over the scale its numbers are on.
const IndexHead = ({ label }) => (
  <>
    {label}{" "}
    <span className="block normal-case tracking-normal">{OUT_OF_10}</span>
  </>
);

const isRegistered = (p) => !String(p.athleteId).startsWith("roster-");

// Figma column widths (1196px in all); the table scrolls sideways below that.
// The pillar columns (third entry true) are wide enough for "(out of 10)".
const ROSTER_COLUMNS = [
  ["Player", 226],
  ["Classification", 100],
  ["CacheScore", 110],
  ["Trend", 60],
  ["Performance", 120, true],
  ["Visibility", 90, true],
  ["Off-court", 90, true],
  ["CacheValue", 100],
  ["NILx value", 100],
  ["Swing archetype", 130],
  ["Status", 70],
];

// Verifying a player is a coach action the Figma frame leaves out: a small
// link under the status dot of an activated player the coach has not
// verified yet.
const VerifyLink = ({ busy, onClick, className = "" }) => (
  <button
    type="button"
    disabled={busy}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`whitespace-nowrap bm-label-xs text-bm-orange hover:underline disabled:opacity-50 ${className}`.trim()}
  >
    {busy ? "Verifying..." : "Verify"}
  </button>
);

const RosterRow = ({ p, archetype, onOpen, onVerify, verifying }) => {
  const active = p.status === "active";
  const open = active && isRegistered(p) && onOpen ? () => onOpen(p) : null;
  const classification = rosterClassOf(p);
  const centered = "bm-td text-center";
  return (
    <tr onClick={open || undefined} className={`${active ? "bg-white" : "bg-bm-subtle"} ${open ? "cursor-pointer hover:bg-bm-subtle" : ""}`}>
      <td className="bm-td">
        <PlayerCell
          p={p}
          onClick={
            open &&
            ((e) => {
              e.stopPropagation();
              open();
            })
          }
        />
      </td>
      <td className={`${centered} bm-body-m ${classification === EMPTY_VALUE ? "text-bm-tertiary" : "text-bm-ink"}`}>{classification}</td>
      <td className={centered}>{active ? <Pillar value={p.score} /> : <Muted />}</td>
      <td className={centered}>{active ? <TrendCell trend={p.trend} /> : <Muted />}</td>
      <td className={centered}>
        <IndexPillar value={p.performance} />
      </td>
      <td className={centered}>
        <IndexPillar value={p.visibility} />
      </td>
      <td className={centered}>
        <IndexPillar value={p.offCourt} />
      </td>
      <td className={`${centered} bm-body-m text-bm-ink`}>{active && p.cacheValue != null ? fmtMoneyCompact(p.cacheValue) : <Muted />}</td>
      <td className={`${centered} bm-body-m text-bm-ink`}>{active && p.nilx != null ? fmtMoneyCompact(p.nilx) : <Muted />}</td>
      <td className={centered}>
        <ArchetypeBadge archetype={archetype} />
      </td>
      <td className={centered}>
        <span className="inline-flex flex-col items-center gap-1">
          <StatusDot status={p.status} title={p.verified ? "Active · verified" : undefined} />
          {active && isRegistered(p) && onVerify && !p.verified && <VerifyLink busy={verifying} onClick={() => onVerify(p)} />}
        </span>
      </td>
    </tr>
  );
};

// ------------------------------------------------------ Phone roster cards
// Card figure labels stay on one line. From 352px up every label fits its
// column; on narrower screens a long one such as PERFORMANCE ends in an
// ellipsis instead of breaking mid-word or running into the next column.
const CARD_LABELS = "[&_p]:truncate";

// A badge in a phone card wraps inside its column instead of running out of
// the card (long brand categories such as "Insurance / Financial Services").
const WRAP_BADGE = "!h-auto min-h-[22px] max-w-full py-1 !whitespace-normal";

// A card figure: the value, or the tertiary en dash when there is none.
const cardFigure = (label, value) =>
  value == null || value === "" ? { label, value: EMPTY_VALUE, valueClass: "bm-label-l text-bm-tertiary" } : { label, value };

// The 8px status dot beside the name (2407:1005), in place of the table's
// Status column.
const CardStatusDot = ({ p }) => {
  const active = p.status === "active";
  const label = active ? (p.verified ? "Active · verified" : "Active") : "Pending activation";
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ background: active ? STATUS_COLORS.active : STATUS_COLORS.pending }}
    />
  );
};

// One roster spot on a phone (2407:998): avatar, name with the status dot,
// "PG · #23 · 2027" (the table's Classification column folds into it: a
// college athlete's level such as JR, else the grad year, else the class
// label), the
// archetype badge (nothing when there is none), a rule, then two rows of
// three figures. The same placeholders as the table row: CacheScore, trend,
// CacheValue and NILx only once activated. An activated, registered player
// opens the profile from anywhere on the card: the name is the button and
// its ::after covers the card. Pending cards stay white (the frame) and are
// not interactive. Verify is a web-only action under the figures.
const RosterCard = ({ p, archetype, onOpen, onVerify, verifying }) => {
  const active = p.status === "active";
  const open = active && isRegistered(p) && onOpen ? () => onOpen(p) : null;
  const classification = rosterClassOf(p);
  const sub = [playerSubtitle(p), classification !== EMPTY_VALUE ? classification : null].filter(Boolean).join(" · ") || EMPTY_VALUE;
  const meta = ARCHETYPE_META[archetype];
  const name = `${p.name} ${p.surname}`;
  const trend = active && trendLabel(p.trend).arrow ? <TrendCell trend={p.trend} type="bm-body-xs" /> : null;
  const score = active && p.score != null ? (
    <span className="flex items-center gap-[6px] whitespace-nowrap">
      <span>{p.score}</span>
      {trend}
    </span>
  ) : null;
  return (
    <MobileCard as="li" className="relative">
      <div className="flex items-center gap-3">
        <Avatar src={p.avatar} initials={initialsOf(p)} size={32} tone="outline" />
        <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
          <span className="flex items-center gap-[6px]">
            {open ? (
              <button
                type="button"
                onClick={open}
                className="min-w-0 text-left bm-h-xs text-bm-ink after:absolute after:inset-0 after:rounded-[16px] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-bm-orange"
              >
                {name}
              </button>
            ) : (
              <span className="min-w-0 bm-h-xs text-bm-ink">{name}</span>
            )}
            <CardStatusDot p={p} />
          </span>
          <span className="bm-body-s text-bm-muted">{sub}</span>
        </div>
        {meta && (
          <Badge tone="outline" dot={meta.color} className="shrink-0">
            {meta.label}
          </Badge>
        )}
      </div>
      <ListRule />
      <div className="flex flex-col gap-3">
        <MetaCols className={CARD_LABELS} items={[cardFigure("CacheScore", score), cardFigure("Performance", indexFigure(p.performance)), cardFigure("Visibility", indexFigure(p.visibility))]} />
        <MetaCols
          className={CARD_LABELS}
          items={[
            cardFigure("Off-court", indexFigure(p.offCourt)),
            cardFigure("CacheValue", active && p.cacheValue != null ? fmtMoneyCompact(p.cacheValue) : null),
            cardFigure("NILx value", active && p.nilx != null ? fmtMoneyCompact(p.nilx) : null),
          ]}
        />
      </div>
      {active && isRegistered(p) && onVerify && !p.verified && (
        <span className="relative z-[1] self-end">
          <VerifyLink busy={verifying} onClick={() => onVerify(p)} className="-my-2 py-2 pl-3" />
        </span>
      )}
    </MobileCard>
  );
};

// The roster as cards (2407:978), for RosterTable inside the phone theme.
const RosterCards = ({ players, archetypes, onOpen, onVerify, verifyingId, emptyText }) => {
  if (!players.length) return <MobileEmpty>{emptyText}</MobileEmpty>;
  return (
    <ul aria-label="Players" className="flex flex-col gap-3">
      {players.map((p) => (
        <RosterCard key={p.athleteId} p={p} archetype={archetypes[p.athleteId]} onOpen={onOpen} onVerify={onVerify} verifying={verifyingId === p.athleteId} />
      ))}
    </ul>
  );
};

// Roster table (Figma 02 · Team Roster): every roster spot with its
// pillars, values, swing archetype and status. Pending rows keep the name
// and show placeholders for what only activation brings. `archetypes`
// (athleteId -> swing archetype) should come from the whole roster, so a
// filtered view keeps each player's quadrant; it defaults to the rows shown.
// Inside the Coach Mode phone theme it renders the player cards instead
// (D-c3: Rep Mode has no phone theme and keeps the table).
export const RosterTable = ({ players, archetypes, onOpen, onVerify, verifyingId, emptyText = "No players in this view yet." }) => {
  const phone = usePhoneTheme();
  const swing = archetypes || swingArchetypes(players);
  if (phone) {
    return <RosterCards players={players} archetypes={swing} onOpen={onOpen} onVerify={onVerify} verifyingId={verifyingId} emptyText={emptyText} />;
  }
  return (
    <Card padding="p-0" className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1196px] table-fixed [&_td]:!py-3">
          <colgroup>
            {ROSTER_COLUMNS.map(([label, width]) => (
              <col key={label} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {ROSTER_COLUMNS.map(([label, , index], i) => (
                <th key={label} className={`bm-th !h-11 ${i === 0 ? "text-left" : "text-center"}`}>
                  {index ? <IndexHead label={label} /> : label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.length === 0 && (
              <tr>
                <td colSpan={ROSTER_COLUMNS.length} className="bm-td !py-10 text-center bm-body-m text-bm-muted">
                  {emptyText}
                </td>
              </tr>
            )}
            {players.map((p) => (
              <RosterRow
                key={p.athleteId}
                p={p}
                archetype={swing[p.athleteId]}
                onOpen={onOpen}
                onVerify={onVerify}
                verifying={verifyingId === p.athleteId}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export const StatCard = ({ label, badge, badgeTone = "green", value, className = "" }) => (
  <Card padding="px-6 py-5" className={`flex flex-col gap-2 ${className}`}>
    <div className="flex items-center justify-between gap-3">
      <Overline>{label}</Overline>
      {badge && <span className={`bm-label-xs ${badgeTone === "green" ? "text-[#1f9d55]" : badgeTone === "muted" ? "text-bm-tertiary" : "text-bm-critical"}`}>{badge}</span>}
    </div>
    <div className="bm-h-m text-bm-ink">{value}</div>
  </Card>
);

// The phone insight card (2409:1264, 2411:2000): a 56px photo (or `logo` on
// a grey disc, or a plain grey disc when there is no player yet) beside the
// overline, the name line, the optional value and the badge, which wraps
// instead of running out of the card.
const HighlightRow = ({ eyebrow, avatar, initials, logo, title, value, chip, chipDot }) => {
  let media;
  if (logo) media = <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-bm-strong">{logo}</span>;
  else if (avatar || initials) media = <Avatar src={avatar} initials={initials} size={56} tone="outline" />;
  else media = <span aria-hidden="true" className="h-14 w-14 shrink-0 rounded-full bg-bm-mutedbg" />;
  return (
    <div className={`${PHONE_CARD} flex items-center gap-[14px] py-4`}>
      {media}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1 break-words">
        <Overline>{eyebrow}</Overline>
        <span className="bm-h-xs text-bm-ink">{title}</span>
        {value !== undefined && <span className="bm-num-m text-bm-ink">{value}</span>}
        {chip && (
          <Badge tone="outline" dot={chipDot} className={WRAP_BADGE}>
            {chip}
          </Badge>
        )}
      </div>
    </div>
  );
};

// Insight card (Figma "Highest CacheScore™", "Brand fit leader"): an
// overline, an 84px photo (or `logo` on a grey disc), the name line, an
// optional value and a badge.
const HighlightTileCard = ({ eyebrow, avatar, initials, logo, title, value, chip, chipDot }) => (
  <Card padding="p-5" className="flex flex-col items-center gap-[10px] text-center">
    <Overline>{eyebrow}</Overline>
    {logo ? (
      <span className="flex h-[84px] w-[84px] items-center justify-center rounded-full bg-bm-strong">{logo}</span>
    ) : (
      <Avatar src={avatar} initials={initials || EMPTY_VALUE} size={84} tone="outline" />
    )}
    <span className="bm-h-s text-bm-ink">{title}</span>
    {value !== undefined && <span className="bm-num-l text-bm-ink">{value}</span>}
    {chip && (
      <Badge tone="outline" dot={chipDot}>
        {chip}
      </Badge>
    )}
  </Card>
);

// `layout="row"` picks the phone card; Coach Team opts in inside the phone
// theme, Rep Mode keeps the tile.
export const HighlightTile = ({ layout = "tile", ...props }) => (layout === "row" ? <HighlightRow {...props} /> : <HighlightTileCard {...props} />);

// Swing Composition (Figma 2253:3096). The plot is a fixed 425 x 280 frame
// (fluid below that width): four tinted quadrants, black left and bottom
// axes, the dashed "priced right" diagonal and one dot per activated player
// with a CacheValue and a NILx value, coloured by the quadrant it sits in
// (see swingPoints).
// Each dot opens the athlete hover card.
const QUADRANTS = [
  { key: "star", left: "0%", top: "0%" },
  { key: "complete", left: "50%", top: "0%" },
  { key: "dev", left: "0%", top: "50%" },
  { key: "gem", left: "50%", top: "50%" },
];
const AXIS_LABEL = "absolute whitespace-nowrap font-inter text-[11.5px] font-bold text-black";
const PHONE_AXIS_LABEL = "absolute whitespace-nowrap bm-label-xs text-bm-ink";

// Plot geometry. Desktop: the 425 x 280 frame. Phone (2406:965): a 306 x 212
// frame (fluid to the card width), the axes 18px from the left and 22px
// from the bottom, 144 x 93 quadrants, 14px dots and 10px axis labels.
const PLOT = {
  desktop: {
    frame: "relative aspect-[425/280] w-full max-w-[425px] shrink-0",
    area: "absolute bottom-[19px] left-[20px] right-[10px] top-[9px]",
    yAxis: "absolute bottom-[19px] left-[20px] top-0 w-px bg-black",
    xAxis: "absolute bottom-[19px] left-[20px] right-0 h-px bg-black",
    xLabel: `${AXIS_LABEL} bottom-0 right-[10px]`,
    yLabel: `${AXIS_LABEL} left-[7px] top-[44px] -translate-x-1/2 -translate-y-1/2 -rotate-90`,
    empty: "absolute inset-x-[20px] top-1/2 -translate-y-1/2 text-center bm-body-s text-bm-muted",
    dot: 18,
  },
  phone: {
    frame: "relative aspect-[306/212] w-full shrink-0",
    area: "absolute bottom-[22px] left-[18px] right-0 top-[4px]",
    yAxis: "absolute bottom-[22px] left-[18px] top-0 w-px bg-black",
    xAxis: "absolute bottom-[22px] left-[18px] right-0 h-px bg-black",
    xLabel: `${PHONE_AXIS_LABEL} bottom-[2px] right-0`,
    yLabel: `${PHONE_AXIS_LABEL} left-[7px] top-[31px] -translate-x-1/2 -translate-y-1/2 -rotate-90`,
    empty: "absolute inset-x-[18px] top-1/2 -translate-y-1/2 text-center bm-body-s text-bm-muted",
    dot: 14,
  },
};

const SwingDot = ({ point, teamName, dot }) => {
  const meta = ARCHETYPE_META[point.archetype];
  return (
    <AthleteHoverCard
      player={point.player}
      archetype={point.archetype}
      teamName={teamName}
      className="absolute flex"
      style={{ left: `calc(${point.x}% - ${dot / 2}px)`, top: `calc(${100 - point.y}% - ${dot / 2}px)` }}
    >
      <button
        type="button"
        aria-label={`${point.player.name} ${point.player.surname}, ${meta.label}`}
        className="rounded-full border-2 border-white"
        style={{ width: dot, height: dot, background: meta.dotColor || meta.color }}
      />
    </AthleteHoverCard>
  );
};

const SwingPlot = ({ points, teamName, geometry = PLOT.desktop }) => (
  <div className={geometry.frame}>
    <div className={geometry.area}>
      {QUADRANTS.map((q) => (
        <span key={q.key} className="absolute h-1/2 w-1/2" style={{ left: q.left, top: q.top, background: ARCHETYPE_META[q.key].color, opacity: 0.1 }} />
      ))}
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <line x1="0" y1="100" x2="100" y2="0" stroke="#BABABA" strokeDasharray="5 5" vectorEffect="non-scaling-stroke" />
      </svg>
      {points.map((pt) => (
        <SwingDot key={pt.player.athleteId} point={pt} teamName={teamName} dot={geometry.dot} />
      ))}
    </div>
    <span className={geometry.yAxis} />
    <span className={geometry.xAxis} />
    <span className={geometry.xLabel}>CacheValue™</span>
    <span className={geometry.yLabel}>NILxValue™</span>
    {points.length === 0 && <span className={geometry.empty}>Dots appear once activated players carry a CacheValue.</span>}
  </div>
);

// Phone legend (2406:982): one column of grey tiles, the badge at its own
// width on the left, the same copy as the desktop cards.
const SwingLegendPhone = () => (
  <div className="flex flex-col gap-[10px]">
    {Object.entries(ARCHETYPE_META).map(([key, meta]) => (
      <div key={key} className="flex flex-col gap-2 rounded-[12px] bg-bm-subtle px-[14px] py-3">
        <Badge tone="outline" dot={meta.color} className="self-start">
          {meta.label}
        </Badge>
        <div className="bm-body-s text-bm-ink">
          <p>{meta.blurb}</p>
          <p className="mt-4">
            <b>CacheValue:</b> {meta.cacheValueLabel}
          </p>
          <p>
            <b>Swing:</b> {meta.swing}
          </p>
          <p className="mt-4">{meta.copy}</p>
        </div>
      </div>
    ))}
  </div>
);

const SwingLegend = () => (
  <div className="grid min-w-0 flex-1 grid-cols-2 gap-4 xl:grid-cols-4">
    {Object.entries(ARCHETYPE_META).map(([key, meta]) => (
      <div key={key} className="flex flex-col gap-[10px] rounded-[12px] border border-bm-border bg-white p-[18px]">
        <Badge tone="outline" dot={meta.color} className="w-full justify-center">
          {meta.label}
        </Badge>
        <div className="font-inter text-[11px] leading-[16.5px] text-bm-ink">
          <p>{meta.blurb}</p>
          <p className="mt-[16.5px]">
            <b>CacheValue:</b> {meta.cacheValueLabel}
          </p>
          <p>
            <b>Swing:</b> {meta.swing}
          </p>
          <p className="mt-[16.5px]">{meta.copy}</p>
        </div>
      </div>
    ))}
  </div>
);

const SWING_COPY =
  "Distance from the diagonal is the swing. Below the line, the NIL market underprices the athlete, the buy signal. Above it, NILx outruns on-court value: a pure brand asset.";

// Phones (2406:962): the phone card, a bm-h-s title, bm-body-m copy, the
// 306 x 212 plot and the one-column legend under it.
export const SwingComposition = ({ players, teamName = "", title = "Swing Composition" }) => {
  const phone = usePhoneTheme();
  if (phone) {
    return (
      <div className={`${PHONE_CARD} flex flex-col gap-[14px] py-4`}>
        <h2 className="bm-h-s text-bm-ink">{title}</h2>
        <p className="bm-body-m text-bm-muted">{SWING_COPY}</p>
        <SwingPlot points={swingPoints(players)} teamName={teamName} geometry={PLOT.phone} />
        <SwingLegendPhone />
      </div>
    );
  }
  return (
    <Card className="flex flex-col gap-4">
      <h2 className="bm-h-m text-bm-ink">{title}</h2>
      <p className="bm-body-l text-bm-muted">{SWING_COPY}</p>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <SwingPlot points={swingPoints(players)} teamName={teamName} />
        <SwingLegend />
      </div>
    </Card>
  );
};

// Team vs population charts (Figma "Average Value by Position" and "Team vs.
// Population Engagement by Platform"): dark grey team bars, light grey
// population bars (the engagement chart draws its population benchmark as
// an ink tick instead, EngagementByPlatform), dashed grid, ink axes with
// tick marks.
const TEAM_BAR = "#757575";
const POPULATION_BAR = "#B8B8BC";
const AXIS = { stroke: "#2c2c2c" };
const TICK = { fill: "#2c2c2c", fontSize: 11, fontFamily: "Inter, sans-serif" };

// Phone charts (2409:1180, 2411:1357): 10px muted ticks, no tick marks, the
// dashed grid in bm-mutedbg and a solid ink baseline, 16px / 20px bars with
// a 2px radius, sized to the 306px card.
const PHONE_TICK = { fill: "#757575", fontSize: 10, fontFamily: "Inter, sans-serif" };
const PHONE_GRID = { strokeDasharray: "4 4", stroke: "#F2F2F4" };

// Phones space the two badges 8px apart.
const ChartLegend = ({ phone = false }) => (
  <div className={`flex flex-wrap items-center ${phone ? "gap-2" : "gap-5"}`}>
    <Badge tone="outline" dot={TEAM_BAR}>
      Team Average
    </Badge>
    <Badge tone="outline" dot={POPULATION_BAR}>
      Population Average
    </Badge>
  </div>
);

const moneyTick = (v) => (v === 0 ? "$0K" : fmtMoneyCompact(v));

// Average CacheValue per position group (G / F / C): the team's activated
// players against every scored athlete on the platform.
export const PositionValueChart = ({ team, population }) => {
  const phone = usePhoneTheme();
  const popByPos = Object.fromEntries((population || []).map((r) => [r.position, r.avg]));
  const data = (team || []).map((r) => ({ position: r.position, team: r.avg, population: popByPos[r.position] ?? null }));
  const ticks = niceTicks(Math.max(0, ...data.flatMap((d) => [d.team || 0, d.population || 0])));
  if (phone) {
    // 2409:1180: a 236px chart, the value labels ending 6px left of the
    // plot, horizontal grid lines only, the two bars of a group 2px apart.
    return (
      <div className={`${PHONE_CARD} flex flex-col gap-3 py-4`}>
        <h2 className="bm-h-s text-bm-ink">Average Value by Position (G/F/C)</h2>
        <ChartLegend phone />
        {data.length ? (
          <div className="h-[236px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} barGap={2} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} {...PHONE_GRID} />
                <XAxis dataKey="position" axisLine={AXIS} tickLine={false} tickSize={4} height={32} tick={PHONE_TICK} />
                <YAxis domain={[0, ticks[ticks.length - 1]]} ticks={ticks} tickFormatter={moneyTick} axisLine={false} tickLine={false} tickSize={4} tick={PHONE_TICK} width={46} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="team" name="Team average" fill={TEAM_BAR} radius={[2, 2, 0, 0]} barSize={16} isAnimationActive={false} />
                <Bar dataKey="population" name="Population average" fill={POPULATION_BAR} radius={[2, 2, 0, 0]} barSize={16} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="bm-body-m text-bm-muted">Position values appear once activated players carry a CacheValue.</p>
        )}
      </div>
    );
  }
  return (
    <Card className="flex flex-col gap-4">
      <h2 className="bm-h-m text-bm-ink">Average Value by Position (G/F/C)</h2>
      <ChartLegend />
      {data.length ? (
        <div className="h-[380px] pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barGap={0} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="position" axisLine={AXIS} tickLine={AXIS} tickSize={14} tick={TICK} />
              <YAxis domain={[0, ticks[ticks.length - 1]]} ticks={ticks} tickFormatter={moneyTick} axisLine={AXIS} tickLine={AXIS} tickSize={8} tick={TICK} width={60} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="team" name="Team average" fill={TEAM_BAR} radius={[7, 7, 0, 0]} barSize={27} isAnimationActive={false} />
              <Bar dataKey="population" name="Population average" fill={POPULATION_BAR} radius={[7, 7, 0, 0]} barSize={27} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="bm-body-m text-bm-muted">Position values appear once activated players carry a CacheValue.</p>
      )}
    </Card>
  );
};

// CacheScore pillars per player (Rep Mode). The swing archetype is the
// same CacheValue vs NILx quadrant as the roster table and the Swing
// Composition chart; `archetypes` works as on RosterTable.
export const PillarsTable = ({ players, archetypes }) => {
  const swing = archetypes || swingArchetypes(players);
  return (
    <Card padding="p-0" className="overflow-hidden">
      <div className="px-6 pt-6">
        <h2 className="bm-h-m text-bm-ink">CacheScore™ Pillars</h2>
      </div>
      <div className="overflow-x-auto px-6 pb-6 pt-3">
        <table className="w-full min-w-[560px] bm-table-compact">
          <thead>
            <tr className="bg-bm-subtle">
              <th className="bm-th text-left">Player</th>
              <th className="bm-th text-left">
                <IndexHead label="Performance" />
              </th>
              <th className="bm-th text-left">
                <IndexHead label="Visibility" />
              </th>
              <th className="bm-th text-left">
                <IndexHead label="Off-court" />
              </th>
              <th className="bm-th text-right">Swing archetype</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.athleteId} className="border-t border-bm-border">
                <td className="bm-td">
                  <PlayerCell p={p} />
                </td>
                <td className="bm-td">
                  <IndexPillar value={p.performance} />
                </td>
                <td className="bm-td">
                  <IndexPillar value={p.visibility} />
                </td>
                <td className="bm-td">
                  <IndexPillar value={p.offCourt} />
                </td>
                <td className="bm-td text-right">
                  <ArchetypeBadge archetype={swing[p.athleteId]} />
                </td>
              </tr>
            ))}
            {players.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center bm-body-m text-bm-muted">
                  Pillars appear once players activate their profiles.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// Engagement per platform: the team bar, and the population benchmark as an
// ink tick across the team bar's band at its value, drawn over the bar, so
// the benchmark shows whether the team is above or below it (notes 10/21
// D5; it was a bar under the team bar, hidden whenever the team was above
// it). The axis grows to the largest team value or benchmark, so neither is
// ever clipped.
const ENGAGEMENT_STEP = 3;

// The mark the population Bar draws in place of its rectangle (a recharts
// `shape`, called with the bar's geometry): a `thick` px tick at the bar's
// end, `overhang` px past the band on each side, with a white edge so it
// reads on the grey team bar. The edge is painted under the ink
// (paint-order), so the tick keeps its full `thick` px of ink, 2px on a
// phone. A platform with no benchmark draws nothing.
const benchmarkTick = (thick, overhang) => (props) => {
  const { x, y, width, height, value } = props;
  const end = Number(x) + Number(width);
  if (value === null || value === undefined || ![end, Number(y), Number(height)].every(Number.isFinite)) return null;
  return (
    <rect
      className="engagement-benchmark"
      x={end - thick / 2}
      y={Number(y) - overhang}
      width={thick}
      height={Number(height) + 2 * overhang}
      rx={thick / 2}
      fill={CHART.ink}
      stroke={CHART.white}
      strokeWidth={1}
      paintOrder="stroke"
    />
  );
};
const DESKTOP_BENCHMARK = benchmarkTick(3, 5);
const PHONE_BENCHMARK = benchmarkTick(2, 4);

// The legend: the team bar's dot, and the tick the benchmark is drawn as.
const EngagementLegend = ({ phone = false }) => (
  <div className={`flex flex-wrap items-center ${phone ? "gap-2" : "gap-5"}`}>
    <Badge tone="outline" dot={TEAM_BAR}>
      Team Average
    </Badge>
    <Badge tone="outline">
      <span aria-hidden="true" data-swatch="tick" className="h-[11px] w-[2px] shrink-0 rounded-full" style={{ background: CHART.ink }} />
      Population Average
    </Badge>
  </div>
);

// The chart in words for screen readers (the bars and the tick carry no
// text): "Instagram: team 4.2%, population 1.9%; TikTok: ...".
const engagementPct = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? `${Math.round(n * 10) / 10}%` : null;
};
const engagementSummary = (data) => {
  const lines = (data || []).map((d) => {
    const team = engagementPct(d.team);
    const population = engagementPct(d.population);
    const parts = [team && `team ${team}`, population && `population ${population}`].filter(Boolean);
    return `${d.label}: ${parts.length ? parts.join(", ") : "no data yet"}`;
  });
  return `Team vs. population engagement by platform${lines.length ? `. ${lines.join("; ")}.` : ": no platforms yet."}`;
};

export const EngagementByPlatform = ({ rows }) => {
  const phone = usePhoneTheme();
  const data = (rows || []).map((r) => ({ label: PLATFORM_LABELS[r.platform] || r.platform, team: r.team ?? null, population: r.benchmark ?? null }));
  const peak = Math.max(0, ...data.flatMap((d) => [d.team || 0, d.population || 0]));
  const top = Math.max(4 * ENGAGEMENT_STEP, Math.ceil(peak / ENGAGEMENT_STEP) * ENGAGEMENT_STEP);
  const ticks = Array.from({ length: top / ENGAGEMENT_STEP + 1 }, (_, i) => i * ENGAGEMENT_STEP);
  const summary = engagementSummary(data);
  if (phone) {
    // 2411:1357: a 196px chart, 20px bars, vertical grid lines only, the
    // platform names ending 6px left of the ink axis.
    return (
      <div className={`${PHONE_CARD} flex flex-col gap-3 py-4`}>
        <h2 className="bm-h-s text-bm-ink">Team vs. Population Engagement by Platform</h2>
        <EngagementLegend phone />
        <div role="img" aria-label={summary} className="h-[196px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" barGap={-20} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid horizontal={false} {...PHONE_GRID} />
              <XAxis type="number" domain={[0, top]} ticks={ticks} tickFormatter={(v) => `${v}%`} axisLine={AXIS} tickLine={false} tickSize={4} height={32} tick={PHONE_TICK} />
              <YAxis type="category" dataKey="label" width={62} axisLine={AXIS} tickLine={false} tickSize={4} tick={PHONE_TICK} />
              <Tooltip formatter={(v, name) => [v == null ? EMPTY_VALUE : `${v}%`, name]} />
              <Bar dataKey="team" name="Team average" fill={TEAM_BAR} barSize={20} radius={[0, 2, 2, 0]} isAnimationActive={false} />
              <Bar dataKey="population" name="Population average" fill={CHART.ink} barSize={20} shape={PHONE_BENCHMARK} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
  return (
    <Card className="flex flex-col gap-4">
      <h2 className="bm-h-m text-bm-ink">Team vs. Population Engagement by Platform</h2>
      <EngagementLegend />
      <div role="img" aria-label={summary} className="h-[323px] pt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" barGap={-32} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#D9D9D9" />
            <XAxis type="number" domain={[0, top]} ticks={ticks} tickFormatter={(v) => `${v}%`} axisLine={AXIS} tickLine={AXIS} tickSize={10} tick={TICK} />
            <YAxis type="category" dataKey="label" width={78} axisLine={AXIS} tickLine={AXIS} tickSize={6} tick={TICK} />
            <Tooltip formatter={(v, name) => [v == null ? EMPTY_VALUE : `${v}%`, name]} />
            <Bar dataKey="team" name="Team average" fill={TEAM_BAR} barSize={32} radius={[0, 7, 7, 0]} isAnimationActive={false} />
            <Bar dataKey="population" name="Population average" fill={CHART.ink} barSize={32} shape={DESKTOP_BENCHMARK} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// One brand category the roster's brand matches point to (Figma
// "BrandFitCard"): name, two-letter tile, players with a match there, fit.
const BrandFitCard = ({ category }) => (
  <div className="flex flex-col items-center gap-[9px] rounded-[8px] border border-bm-border bg-white px-[14px] py-4 text-center">
    <span className="bm-h-s text-bm-ink">{category.name}</span>
    <span className="flex h-11 w-11 items-center justify-center rounded-[8px] bg-bm-mutedbg bm-label-l text-bm-ink">{category.mono}</span>
    <span className="bm-h-xs text-bm-orange">{plural(category.players, "player")}</span>
    <span className="flex items-center gap-2 bm-label-l text-bm-ink">
      <span className="h-[10px] w-[10px] rounded-full" style={{ background: fitTone(category.fit) }} />
      {category.fit}
    </span>
  </div>
);

// Team Brand Category Alignment: the categories of the activated players'
// cached brand matches. While matches are still being computed, a note
// says so (the page refetches on its own).
// Phones (2411:1392): the phone card, a bm-h-s title and two columns 10px
// apart; the fit cards themselves already match the frame.
export const BrandCategoryGrid = ({ categories, computing }) => {
  const phone = usePhoneTheme();
  const list = categories || [];
  const body = (
    <>
      {computing && (
        <span className="flex items-center gap-2 bm-body-s text-bm-muted">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-bm-border border-t-bm-orange" />
          Matching brands to your activated players...
        </span>
      )}
      {list.length > 0 ? (
        <div className={phone ? "grid grid-cols-2 gap-[10px]" : "grid grid-cols-2 gap-3 md:grid-cols-3"}>
          {list.map((c) => (
            <BrandFitCard key={c.name} category={c} />
          ))}
        </div>
      ) : (
        !computing && <p className="bm-body-m text-bm-muted">Brand categories appear once brand matches are computed for your activated players.</p>
      )}
    </>
  );
  if (phone) {
    return (
      <div className={`${PHONE_CARD} flex flex-col gap-3 py-4`}>
        <h2 className="bm-h-s text-bm-ink">Team Brand Category Alignment</h2>
        {body}
      </div>
    );
  }
  return (
    <Card className="flex flex-col gap-4">
      <h2 className="bm-h-m text-bm-ink">Team Brand Category Alignment</h2>
      {body}
    </Card>
  );
};

// One-colour platform glyphs in the Figma order, drawn from the V2 platform
// tiles (assets/brand/edit-hub/menu-*.svg) and filled with currentColor, so a
// text colour class tints them. Each entry is [path, evenodd].
const GLYPH_PATHS = {
  instagram: [
    ["M13.4641 7.30365C13.0336 7.30449 12.6836 6.95594 12.6827 6.52547C12.6819 6.095 13.0304 5.74498 13.4611 5.74413C13.8918 5.74329 14.2418 6.09205 14.2427 6.52252C14.2433 6.95299 13.8947 7.30281 13.4641 7.30365Z", false],
    ["M10.0062 13.3372C8.16326 13.3408 6.6661 11.8497 6.66252 10.0063C6.65894 8.16333 8.1504 6.66594 9.99337 6.66236C11.8368 6.65878 13.3341 8.1507 13.3377 9.99347C13.3413 11.8369 11.8494 13.3336 10.0062 13.3372ZM9.99569 7.83352C8.79948 7.83563 7.83113 8.80756 7.83324 10.004C7.83556 11.2006 8.80769 12.1688 10.0039 12.1665C11.2005 12.1642 12.1689 11.1924 12.1666 9.99579C12.1642 8.79914 11.1921 7.83121 9.99569 7.83352Z", true],
    ["M5.73123 3.85018C6.14402 3.68843 6.61639 3.57765 7.308 3.54501C8.00131 3.51174 8.22264 3.50416 9.98747 3.50079C11.7527 3.49742 11.9741 3.50415 12.6674 3.5349C13.3592 3.56481 13.8318 3.6739 14.2454 3.83396C14.6733 3.99886 15.0364 4.22084 15.3982 4.58118C15.76 4.94194 15.9828 5.30376 16.1498 5.73107C16.3114 6.14428 16.4222 6.61624 16.455 7.30828C16.4879 8.00137 16.4959 8.22251 16.4992 9.98757C16.5026 11.7524 16.4954 11.974 16.4651 12.6677C16.435 13.3591 16.3261 13.8319 16.1661 14.2453C16.0007 14.6733 15.7792 15.0364 15.4189 15.3982C15.0585 15.7602 14.6963 15.9828 14.269 16.15C13.8558 16.3114 13.3838 16.4221 12.6922 16.4552C11.9989 16.488 11.7776 16.4958 10.0121 16.4992C8.24749 16.5026 8.02615 16.4958 7.33286 16.4653C6.64103 16.435 6.16823 16.3261 5.75482 16.1663C5.32689 16.0007 4.96381 15.7794 4.602 15.4188C4.23998 15.0583 4.01696 14.6962 3.85016 14.2689C3.68842 13.8559 3.57806 13.3838 3.545 12.6923C3.51194 11.9988 3.50415 11.7773 3.50078 10.0124C3.4974 8.24736 3.50435 8.02623 3.53468 7.33292C3.56522 6.64088 3.67368 6.16828 3.83374 5.75445C3.99905 5.32671 4.2206 4.96384 4.58137 4.60182C4.9417 4.24001 5.30393 4.01676 5.73123 3.85018ZM6.17667 15.0743C6.40601 15.1625 6.75012 15.2678 7.38383 15.2954C8.06954 15.3251 8.27487 15.3316 10.01 15.3283C11.7458 15.3251 11.9511 15.3177 12.6366 15.2855C13.2697 15.2554 13.6138 15.1488 13.8425 15.0595C14.146 14.941 14.3621 14.7992 14.5891 14.572C14.8161 14.3439 14.9566 14.1272 15.0741 13.8237C15.1626 13.5941 15.2676 13.2498 15.2952 12.6161C15.3253 11.9308 15.3317 11.7253 15.3283 9.98968C15.3251 8.25452 15.3178 8.04897 15.2851 7.36346C15.2552 6.73017 15.1489 6.38604 15.0594 6.15754C14.9408 5.85364 14.7995 5.63799 14.5716 5.41075C14.3437 5.1835 14.127 5.04345 13.8231 4.92594C13.5942 4.83727 13.2497 4.73239 12.6164 4.70481C11.9307 4.67469 11.7251 4.66858 9.98958 4.67195C8.25444 4.67532 8.04911 4.68227 7.3636 4.7147C6.73012 4.74481 6.38642 4.85117 6.15728 4.94068C5.85401 5.05925 5.63794 5.20014 5.41071 5.42823C5.18388 5.65631 5.04341 5.8726 4.9259 6.17671C4.83788 6.40585 4.73194 6.75039 4.70478 7.38368C4.67486 8.0694 4.66855 8.27495 4.67192 10.0101C4.67508 11.7457 4.68245 11.9512 4.71467 12.6363C4.74437 13.27 4.85156 13.6137 4.94065 13.8431C5.05921 14.1461 5.20053 14.3622 5.42819 14.5895C5.65627 14.8159 5.87298 14.9568 6.17667 15.0743Z", true],
  ],
  tiktok: [
    ["M12.8229 3.5H10.4553V12.3551C10.4553 13.4102 9.5447 14.2768 8.41147 14.2768C7.27825 14.2768 6.36762 13.4102 6.36762 12.3551C6.36762 11.3188 7.25801 10.471 8.35077 10.4333V8.21015C5.94266 8.24782 4 10.0754 4 12.3551C4 14.6536 5.98314 16.5 8.43172 16.5C10.8803 16.5 12.8634 14.6348 12.8634 12.3551V7.81448C13.7538 8.41739 14.8465 8.77536 16 8.79421V6.57102C14.2192 6.51449 12.8229 5.15797 12.8229 3.5Z", false],
  ],
  youtube: [
    ["M17.1485 5.22274C17.3967 5.47258 17.575 5.78325 17.6655 6.12364C18 7.38 18 10 18 10C18 10 18 12.62 17.6655 13.8764C17.575 14.2168 17.3967 14.5274 17.1485 14.7773C16.9002 15.0271 16.5907 15.2074 16.2509 15.3C15 15.6364 10 15.6364 10 15.6364C10 15.6364 5 15.6364 3.74909 15.3C3.40929 15.2074 3.09977 15.0271 2.85153 14.7773C2.60328 14.5274 2.425 14.2168 2.33455 13.8764C2 12.62 2 10 2 10C2 10 2 7.38 2.33455 6.12364C2.425 5.78325 2.60328 5.47258 2.85153 5.22274C3.09977 4.9729 3.40929 4.79264 3.74909 4.7C5 4.36364 10 4.36364 10 4.36364C10 4.36364 15 4.36364 16.2509 4.7C16.5907 4.79264 16.9002 4.9729 17.1485 5.22274ZM12.5455 10L8.36365 7.62093V12.3791L12.5455 10Z", true],
  ],
  x: [
    ["M13.4507 4.5H15.2908L11.2708 9.15949L16 15.5H12.297L9.39675 11.6545L6.07815 15.5H4.23696L8.53678 10.5162L4 4.5H7.79697L10.4186 8.01492L13.4507 4.5ZM12.8049 14.3831H13.8245L7.24294 5.55826H6.1488L12.8049 14.3831Z", false],
  ],
};

const platformGlyph = (key) => {
  const Glyph = ({ size = 16, className = "" }) => (
    <svg width={size} height={size} viewBox="2 2 16 16" fill="currentColor" aria-hidden="true" focusable="false" className={`shrink-0 ${className}`.trim()}>
      {GLYPH_PATHS[key].map(([d, evenodd], i) => (
        <path key={i} d={d} fillRule={evenodd ? "evenodd" : undefined} clipRule={evenodd ? "evenodd" : undefined} />
      ))}
    </svg>
  );
  Glyph.displayName = `PlatformGlyph(${key})`;
  return Glyph;
};

export const PLATFORM_GLYPHS = Object.fromEntries(Object.keys(GLYPH_PATHS).map((key) => [key, platformGlyph(key)]));

// Four 22px tiles; a platform the player has not linked is faded.
export const PlatformTiles = ({ platforms }) => (
  <span className="flex items-center gap-[6px]">
    {Object.entries(PLATFORM_GLYPHS).map(([key, Glyph]) => {
      const linked = !!platforms?.[key];
      const label = `${PLATFORM_LABELS[key]} ${linked ? "linked" : "not linked"}`;
      return (
        <span
          key={key}
          role="img"
          aria-label={label}
          title={label}
          className={`flex h-[22px] w-[22px] items-center justify-center rounded-[6px] border border-bm-border bg-bm-mutedbg ${linked ? "" : "opacity-40"}`}
        >
          <Glyph size={14} className="text-bm-muted" />
        </span>
      );
    })}
  </span>
);

const EngagementCell = ({ p }) =>
  p.engagement == null ? (
    <Muted />
  ) : (
    <span className="flex items-center gap-2">
      <span className="bm-body-m text-bm-ink">{Number(p.engagement).toFixed(1)}%</span>
      {p.engagementDelta30d != null && <TrendCell trend={p.engagementDelta30d} suffix="pp" />}
    </span>
  );

// Visibility (third entry true) is a pillar: out of 10, as on the roster.
const NIL_COLUMNS = [
  ["Player", 300],
  ["Visibility", 130, true],
  ["Reach", 150],
  ["Engage %", 160],
  ["Platforms", 160],
  ["Brand category", 150],
  ["NILx value", 98],
];

// ------------------------------------------------------ Phone NIL cards
// One activated player on a phone (2411:1697): avatar, name and "PG · #23"
// with the NILx value on the right, a rule, then Visibility / Reach /
// Engage % and Platforms / Brand category. Engage % keeps the web content
// (the rate and its 30-day change), where the frame shows the change alone.
const NIL_CARD_COLS = `[&>div]:gap-1 ${CARD_LABELS}`;
const NIL_EMPTY = "NIL data appears once players activate their profiles and link a social account.";

const NilCard = ({ p }) => {
  const engagement =
    p.engagement == null ? null : (
      <span className="flex flex-wrap items-center gap-x-[6px]">
        <span>{Number(p.engagement).toFixed(1)}%</span>
        {p.engagementDelta30d != null && <TrendCell trend={p.engagementDelta30d} suffix="pp" />}
      </span>
    );
  const category = p.brandCategory ? (
    <Badge tone="outline" dot={fitTone(p.brandCategoryFit)} className={WRAP_BADGE}>
      {p.brandCategory}
    </Badge>
  ) : null;
  return (
    <MobileCard as="li">
      <div className="flex items-center gap-3">
        <Avatar src={p.avatar} initials={initialsOf(p)} size={32} tone="outline" />
        <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
          <span className="bm-h-xs text-bm-ink">
            {p.name} {p.surname}
          </span>
          <span className="bm-body-s text-bm-muted">{playerSubtitle(p) || EMPTY_VALUE}</span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-[2px] text-right">
          <span className="bm-overline">NILx value</span>
          <span className="bm-num-m text-bm-ink">{fmtMoneyCompact(p.nilx)}</span>
        </div>
      </div>
      <ListRule />
      <MetaCols
        className={NIL_CARD_COLS}
        items={[
          cardFigure("Visibility", indexFigure(p.visibility)),
          cardFigure("Reach", p.reach ? Number(p.reach).toLocaleString("en-US") : null),
          cardFigure("Engage %", engagement),
        ]}
      />
      <MetaCols className={NIL_CARD_COLS} items={[{ label: "Platforms", value: <PlatformTiles platforms={p.platforms} /> }, cardFigure("Brand category", category)]} />
    </MobileCard>
  );
};

// Phones (2411:1695): the section title above the cards, 12px apart.
const NilBreakdownCards = ({ players }) => (
  <div className="flex flex-col gap-3">
    <h2 className="bm-h-s text-bm-ink">Player NIL Breakdown</h2>
    {players.length ? (
      <ul aria-label="Players" className="flex flex-col gap-3">
        {players.map((p) => (
          <NilCard key={p.athleteId} p={p} />
        ))}
      </ul>
    ) : (
      <MobileEmpty>{NIL_EMPTY}</MobileEmpty>
    )}
  </div>
);

// Player NIL Breakdown: the activated players' reach, engagement (with the
// 30-day change once that history exists), platforms, leading brand
// category and NILx value.
const NilBreakdownTableDesktop = ({ players }) => (
  <Card className="flex flex-col gap-4">
    <h2 className="bm-h-m text-bm-ink">Player NIL Breakdown</h2>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1148px] table-fixed [&_td]:!py-3">
        <colgroup>
          {NIL_COLUMNS.map(([label, width]) => (
            <col key={label} style={{ width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {NIL_COLUMNS.map(([label, , index]) => (
              <th key={label} className="bm-th text-left">
                {index ? <IndexHead label={label} /> : label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr key={p.athleteId}>
              <td className="bm-td">
                <PlayerCell p={p} />
              </td>
              <td className="bm-td">
                <IndexPillar value={p.visibility} />
              </td>
              <td className="bm-td bm-body-m text-bm-ink">{p.reach ? Number(p.reach).toLocaleString("en-US") : <Muted />}</td>
              <td className="bm-td">
                <EngagementCell p={p} />
              </td>
              <td className="bm-td">
                <PlatformTiles platforms={p.platforms} />
              </td>
              <td className="bm-td">
                {p.brandCategory ? (
                  <Badge tone="outline" dot={fitTone(p.brandCategoryFit)}>
                    {p.brandCategory}
                  </Badge>
                ) : (
                  <Muted />
                )}
              </td>
              <td className="bm-td bm-num-m text-bm-ink">{fmtMoneyCompact(p.nilx)}</td>
            </tr>
          ))}
          {players.length === 0 && (
            <tr>
              <td colSpan={NIL_COLUMNS.length} className="bm-td !py-8 text-center bm-body-m text-bm-muted">
                {NIL_EMPTY}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);

// Inside the phone theme each player is a card (Rep Mode keeps the table).
export const NilBreakdownTable = ({ players }) => {
  const phone = usePhoneTheme();
  if (phone) return <NilBreakdownCards players={players} />;
  return <NilBreakdownTableDesktop players={players} />;
};

// ------------------------------------------------------ Phone page head
// Header actions on a phone (2406:782, 2407:892, 2412:1431): a row of 52px
// secondary pills that share the width (one alone fills it), then the
// full-width 52px primary. An action is { label, onClick | to, disabled,
// title, key }; a falsy entry in `secondary` is skipped, so a caller can
// write `activeTeam && { ... }`.
const PhoneAction = ({ action, variant, className }) => (
  <Btn size="lg" variant={variant} onClick={action.onClick} to={action.to} disabled={action.disabled} title={action.title} className={className}>
    {action.label}
  </Btn>
);

export const PhoneHeadActions = ({ secondary = [], primary }) => {
  const row = (secondary || []).filter(Boolean);
  return (
    <>
      {row.length > 0 && (
        <div className="flex gap-3">
          {row.map((action) => (
            <PhoneAction key={action.key || action.label} action={action} variant="secondary" className="min-w-0 flex-1 !px-3" />
          ))}
        </div>
      )}
      {primary && <PhoneAction action={primary} variant="primary" className="w-full" />}
    </>
  );
};

// The phone page head of Home, Team and My Circle (2406:779 + 2406:782,
// 2407:889 + 2407:892, 2412:1431): the bm-h-l title with no eyebrow and the
// subtitle (`subSize` "m" on Home, "s" on Team and Circle), then the actions
// stacked full width: `switcher` (the TeamSwitch, 56px on phones), the
// secondary row and the primary. `children` follow the primary (Team's
// league-admin view and season controls). Render it only in the phone
// theme; desktop pages keep PageHeader.
export const CoachPhoneHead = ({ title, sub, subSize = "s", switcher, secondary, primary, className = "", children }) => (
  <MobilePageHead title={title} sub={sub} subSize={subSize} className={className}>
    {switcher}
    <PhoneHeadActions secondary={secondary} primary={primary} />
    {children}
  </MobilePageHead>
);

// Team's roster actions. On phones they render as the stacked phone
// actions (Manage roster and CacheShare in the row, Team Code below), so
// the same element works inside CoachPhoneHead. Team Code opens the team
// code athletes join with, under the same name as on Home.
export const SectionActions = ({ onCacheShare, onTeamCode, onManage }) => {
  const phone = usePhoneTheme();
  if (phone) {
    return (
      <PhoneHeadActions
        secondary={[onManage && { label: "Manage roster", onClick: onManage }, { label: "CacheShare", onClick: onCacheShare }]}
        primary={{ label: "Team Code", onClick: onTeamCode }}
      />
    );
  }
  return (
    <>
      {onManage && (
        <Btn variant="secondary" onClick={onManage}>
          Manage roster
        </Btn>
      )}
      <span className="flex items-center gap-[10px]">
        <Btn variant="secondary" onClick={onCacheShare}>
          CacheShare
        </Btn>
        <Btn onClick={onTeamCode}>Team Code</Btn>
      </span>
    </>
  );
};

// Team switch (Figma 01 · Home header): the teams of the current view,
// labelled "Team · League". Choosing one updates the shared Coach Mode
// selection, which reloads the page. On phones it is the full-width 56px
// select (2406:783) with the 14 x 8 chevron and 16px text.
export const TeamSwitch = ({ selection, onSelect, disabled = false }) => {
  const phone = usePhoneTheme();
  const teams = teamsForView(selection.teams, selection.view);
  if (!teams.length) return null;
  return (
    <Select
      value={teamKey(selection.team || teams[0])}
      onChange={(e) => onSelect(teams.find((t) => teamKey(t) === e.target.value))}
      disabled={disabled}
      aria-label="Select team"
      className={phone ? "bm-input-lg truncate" : "!w-auto max-w-[320px] truncate"}
    >
      {teams.map((t) => (
        /* One program can be several squads (17U / 16U / 15U), one per league. */
        <option key={teamKey(t)} value={teamKey(t)}>
          {teamOptionLabel(t)}
        </option>
      ))}
    </Select>
  );
};

// Shown when the live roster feed is down and the page serves the last
// saved roster. Phones use the phone card with the badge above the text.
export const SavedRosterNotice = ({ asOf }) => {
  const phone = usePhoneTheme();
  const badge = (
    <Badge tone="attention" size="m">
      Saved roster
    </Badge>
  );
  const text = (
    <p className="bm-body-m text-bm-muted">
      The live roster feed is unavailable right now, so this is your roster as last synced on{" "}
      {new Date(asOf).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}. It refreshes on its own once the feed is back.
    </p>
  );
  if (phone) {
    return (
      <div className={`${PHONE_CARD} flex flex-col items-start gap-2 py-4`}>
        {badge}
        {text}
      </div>
    );
  }
  return (
    <Card padding="px-5 py-4" className="flex items-start gap-3">
      {badge}
      {text}
    </Card>
  );
};

// In place of a page's team data while the coach has no authority over the
// team (awaiting verification): what opens later, and the way to verify.
// Phones: the phone card and a full-width 52px button.
export const LockedCard = ({ title, children }) => {
  const phone = usePhoneTheme();
  if (phone) {
    return (
      <div className={`${PHONE_CARD} flex flex-col items-start gap-3 py-4`}>
        <Overline>{title}</Overline>
        <p className="bm-body-m text-bm-muted">{children}</p>
        <Btn variant="secondary" size="lg" to="/coach/profile" className="w-full">
          Profile and verification
        </Btn>
      </div>
    );
  }
  return (
    <Card className="flex flex-col items-start gap-3">
      <Overline>{title}</Overline>
      <p className="bm-body-m text-bm-muted">{children}</p>
      <Btn variant="secondary" to="/coach/profile">
        Profile and verification
      </Btn>
    </Card>
  );
};
